// What a cleanup run actually freed, in words somebody can act on.
//
// The button reported "12 photos supprimées" — a number that tells nobody
// whether running it was worth anything. Owner, 2026-08-24: show "how many GB
// we did saved at the end".
//
// ⚠️ THE UNIT FOLLOWS THE NUMBER, NOT THE REQUEST. "GB" was the ask, but a run
// that frees 40 MB shown as "0,04 GB" reads as nothing happened, and one that
// frees 900 KB shown as "0,00 GB" reads as broken. The point is a figure the
// reader can judge at a glance, which means the unit has to fit it.

export interface CleanupOutcome {
  photos_deleted: number;
  pdfs_deleted: number;
  bytes_freed: number;
  /** Still eligible after this run — one pass is capped. */
  photos_remaining?: number;
  /** Delivered orders whose PDFs were kept on purpose ("manuellement uniquement"). */
  pdfs_skipped_manual?: number;
}

export interface FreedSpace {
  value: number;
  unit: 'KB' | 'MB' | 'GB';
  /** Ready to render, e.g. "1,4 GB". */
  label: string;
}

/**
 * Bytes → the largest unit that still shows a meaningful figure.
 *
 * ⚠️ ONE DECIMAL BELOW TEN, NONE ABOVE. "1,4 GB" is informative; "1,437 GB" is
 * noise, and "14,2 GB" is false precision on a number that came from summing
 * file sizes.
 */
export function formatFreed(bytes: number | null | undefined): FreedSpace {
  const raw = Number(bytes);
  const b = Number.isFinite(raw) && raw > 0 ? raw : 0;
  const GB = 1e9;
  const MB = 1e6;
  const KB = 1e3;

  const { value, unit } = b >= GB
    ? { value: b / GB, unit: 'GB' as const }
    : b >= MB
      ? { value: b / MB, unit: 'MB' as const }
      : { value: b / KB, unit: 'KB' as const };

  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  // French decimal comma — this screen is French throughout.
  return { value: rounded, unit, label: `${String(rounded).replace('.', ',')} ${unit}` };
}

/**
 * Did this run actually do anything?
 *
 * ⚠️ NEVER CELEBRATE A NO-OP. Most runs of a retention cleanup delete nothing,
 * because most of the time nothing has aged past the rule. A popup that cheers
 * "0 KB freed!" trains the reader to dismiss the one that matters.
 */
export function didSomething(r: CleanupOutcome): boolean {
  return (r.photos_deleted > 0 || r.pdfs_deleted > 0) && r.bytes_freed > 0;
}

/** The line under the headline figure. Lists only what actually went — a
 *  sentence carrying "0 PDFs" reads as a failure report. */
export function freedDetail(r: CleanupOutcome): string {
  const parts: string[] = [];
  if (r.photos_deleted > 0) parts.push(`${r.photos_deleted} photo${r.photos_deleted > 1 ? 's' : ''}`);
  if (r.pdfs_deleted > 0) parts.push(`${r.pdfs_deleted} PDF${r.pdfs_deleted > 1 ? 's' : ''}`);
  if (parts.length === 0) return 'Rien à supprimer pour le moment.';
  const plural = parts.length > 1 || r.photos_deleted > 1 || r.pdfs_deleted > 1;
  return `${parts.join(' et ')} supprimé${plural ? 's' : ''}.`;
}

/**
 * The sentence that stops a partial run reading like a finished one.
 *
 * ⚠️ "1000 photos supprimées" WAS A LIE OF OMISSION. PostgREST caps an unranged
 * select at 1000 rows, so the cleanup deleted exactly that and left 6,333 —
 * reporting a number that looks like a complete, successful clean. Six more runs
 * would have finished it and nothing on the screen said so.
 */
export function remainingNote(r: CleanupOutcome): string {
  const left = Number(r.photos_remaining) || 0;
  if (left <= 0) return '';
  return `Il reste ${left.toLocaleString('fr-FR')} photo${left > 1 ? 's' : ''} à supprimer — relancez le nettoyage.`;
}

/**
 * Why zero PDFs went, when zero went on purpose.
 *
 * ⚠️ « MANUELLEMENT UNIQUEMENT » MEANS KEEP. The screen promises the PDFs stay
 * available indefinitely, so the button must not delete them — but reporting a
 * bare "0 PDFs supprimés" reads as a failure, which is exactly how it was read.
 */
export function pdfSkipNote(r: CleanupOutcome): string {
  const kept = Number(r.pdfs_skipped_manual) || 0;
  if (kept <= 0) return '';
  return `${kept} PDF${kept > 1 ? 's' : ''} conservé${kept > 1 ? 's' : ''} `
    + `(réglage « manuellement uniquement »).`;
}
